import type { ItemRole } from './vocabulary.ts'
import { normalizeHeader, readTypeValue } from './vocabulary.ts'

export type SheetRow = Readonly<Record<string, string | undefined>>

/** Headers naming a role column whose values are words ("sample", "NC", ...). */
const ROLE_HEADERS = new Set([
  'type',
  'kind',
  'sampletype',
  'itemtype',
  'typeofsample',
  'sampleorcontrol',
  'samplecontrol',
  'controltype',
  'category',
  'class',
  'sampleclass',
  'role',
  'typ',
  'art',
  'probentyp',
  'probenart',
  'probekontrolle',
  'kontrolltyp',
  'kategorie',
  'rolle'
])

/** Headers asking "is this a control?", where a mark such as `x` or `yes` reads as a control. */
const CONTROL_HEADERS = new Set([
  'control',
  'controls',
  'iscontrol',
  'nc',
  'ntc',
  'isnc',
  'neg',
  'negative',
  'negctrl',
  'negativecontrol',
  'negativecontrols',
  'isnegativecontrol',
  'blank',
  'isblank',
  'kontrolle',
  'negativ',
  'negativkontrolle',
  'istkontrolle'
])

/** Above this many distinct values a column is data, not a role vocabulary. */
const MAX_DISTINCT_VALUES = 6

export type TypeHeaderKind = 'role' | 'control'

export const typeHeaderKind = (header: string): TypeHeaderKind | undefined => {
  const normalized = normalizeHeader(header)
  if (CONTROL_HEADERS.has(normalized)) {
    return 'control'
  }
  return ROLE_HEADERS.has(normalized) ? 'role' : undefined
}

export type TypeValueNote = 'positive-control' | 'unknown' | 'mark-without-control-header'

export type TypeValueEvidence = {
  /** First spelling seen; values are told apart case-insensitively. */
  value: string
  rows: number
  role?: ItemRole
  note?: TypeValueNote
}

export type TypeColumnEvidence = {
  column: string
  /** Set when the header itself names a role column. */
  headerKind?: TypeHeaderKind
  /** `control-flag`: the column marks controls (x / yes / NC) and leaves samples empty or unmarked. */
  reading: 'categorical' | 'control-flag'
  /** Distinct non-empty values, most frequent first. */
  values: TypeValueEvidence[]
  /** Values left without a role; their rows keep whatever type they have. */
  unmapped: string[]
  /** How an empty cell reads; undefined means an empty cell says nothing. */
  emptyMeans?: ItemRole
  emptyRows: number
  controls: number
  samples: number
  unmappedRows: number
  /** The role each row states, by row index; undefined where the row states none. */
  roles: (ItemRole | undefined)[]
}

export type TypeColumnDetection = TypeColumnEvidence & {
  auto: boolean
  /** Why the column was or was not taken automatically. */
  reason: string
}

const cell = (row: SheetRow, column: string) => (row[column] ?? '').trim()

export const analyzeTypeColumn = (column: string, rows: readonly SheetRow[]): TypeColumnEvidence => {
  const headerKind = typeHeaderKind(column)
  const distinct = new Map<string, { value: string; rows: number; reading: ReturnType<typeof readTypeValue> }>()
  let emptyRows = 0
  rows.forEach((row) => {
    const value = cell(row, column)
    if (value === '') {
      emptyRows++
      return
    }
    const key = value.toLowerCase()
    const seen = distinct.get(key)
    if (seen) {
      seen.rows++
    } else {
      distinct.set(key, { value, rows: 1, reading: readTypeValue(value) })
    }
  })

  const readings = [...distinct.values()]
  const isMarker = (reading: ReturnType<typeof readTypeValue>) =>
    reading.kind === 'flag' || (reading.kind === 'role' && reading.role === 'NEGATIVE_CONTROL')
  const reading: TypeColumnEvidence['reading'] =
    headerKind === 'control' && readings.length > 0 && readings.every((entry) => isMarker(entry.reading)) ? 'control-flag' : 'categorical'

  const values: TypeValueEvidence[] = readings
    .map(({ value, rows: count, reading: valueReading }): TypeValueEvidence => {
      switch (valueReading.kind) {
        case 'role':
          return { value, rows: count, role: valueReading.role }
        case 'flag':
          return reading === 'control-flag'
            ? { value, rows: count, role: valueReading.set ? 'NEGATIVE_CONTROL' : 'SAMPLE' }
            : { value, rows: count, note: 'mark-without-control-header' }
        case 'positive-control':
          return { value, rows: count, note: 'positive-control' }
        default:
          return { value, rows: count, note: 'unknown' }
      }
    })
    .sort((a, b) => b.rows - a.rows)

  const mapped = values.filter((entry) => entry.role !== undefined)
  /* A column that only ever names controls leaves its samples blank: "x marks the controls". */
  const onlyControlsNamed = mapped.length > 0 && mapped.every((entry) => entry.role === 'NEGATIVE_CONTROL')
  const emptyMeans: ItemRole | undefined =
    reading === 'control-flag' || (onlyControlsNamed && mapped.length === values.length) ? 'SAMPLE' : undefined

  const roleByKey = new Map(values.map((entry) => [entry.value.toLowerCase(), entry.role]))
  const roles = rows.map((row) => {
    const value = cell(row, column)
    return value === '' ? emptyMeans : roleByKey.get(value.toLowerCase())
  })

  return {
    column,
    headerKind,
    reading,
    values,
    unmapped: values.filter((entry) => entry.role === undefined).map((entry) => entry.value),
    emptyMeans,
    emptyRows,
    controls: roles.filter((role) => role === 'NEGATIVE_CONTROL').length,
    samples: roles.filter((role) => role === 'SAMPLE').length,
    unmappedRows: values.filter((entry) => entry.role === undefined).reduce((sum, entry) => sum + entry.rows, 0),
    roles
  }
}

const judge = (evidence: TypeColumnEvidence): { auto: boolean; reason: string } => {
  const filled = evidence.values.reduce((sum, entry) => sum + entry.rows, 0)
  if (filled === 0) {
    return { auto: false, reason: 'The column is empty.' }
  }
  if (evidence.values.length > MAX_DISTINCT_VALUES) {
    return { auto: false, reason: `${evidence.values.length} different values; a type column has a handful.` }
  }
  if (evidence.controls === 0) {
    return { auto: false, reason: 'No value names a negative control.' }
  }
  if (evidence.headerKind) {
    if (evidence.unmappedRows * 2 > filled) {
      return { auto: false, reason: 'Most values are not words for sample or negative control.' }
    }
    return { auto: true, reason: `The header "${evidence.column}" names sample types.` }
  }
  if (evidence.reading === 'categorical' && evidence.unmapped.length === 0 && evidence.samples > 0) {
    return { auto: true, reason: 'Every value is a word for sample or negative control.' }
  }
  return { auto: false, reason: 'Neither the header nor all of its values name sample types.' }
}

export const judgeTypeColumn = (column: string, rows: readonly SheetRow[]): TypeColumnDetection => {
  const evidence = analyzeTypeColumn(column, rows)
  return { ...evidence, ...judge(evidence) }
}

/**
 * The column to read sample types from, when one qualifies on its own. A named header wins over a column
 * recognised only by its values; among equals, fewer unmapped rows win.
 */
export const detectTypeColumn = (headers: readonly string[], rows: readonly SheetRow[]): TypeColumnDetection | undefined =>
  headers
    .map((header) => judgeTypeColumn(header, rows))
    .filter((detection) => detection.auto)
    .sort((a, b) => Number(!!b.headerKind) - Number(!!a.headerKind) || a.unmappedRows - b.unmappedRows)[0]
